import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { userApi, type User } from '../../api/users';

const STATUS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800', inactive: 'bg-slate-100 text-slate-600',
  suspended: 'bg-amber-100 text-amber-800', archived: 'bg-slate-100 text-slate-500',
};

export function UserDirectoryPage() {
  const [items, setItems] = useState<User[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError('');
    try {
      setItems(q.trim()
        ? (await userApi.search({ q: q.trim(), ...(status ? { status } : {}) })).results
        : await userApi.list(status ? { status, q: q || undefined } : { q: q || undefined }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">User Directory</h1>
          <p className="mt-1 text-sm text-slate-600">Platform users in your tenant scope.</p>
        </div>
        <Link to="/users/new" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Add user</Link>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); void load(); }} className="mt-6 flex flex-wrap gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="min-w-[200px] flex-1 rounded-lg border px-3 py-2" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border px-3 py-2">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <button type="submit" className="rounded-lg border px-4 py-2 text-sm">Search</button>
      </form>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading ? <p className="mt-6">Loading…</p> : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((u) => (
            <Link key={u.id} to={`/users/${u.id}`} className="rounded-xl border bg-white p-4 hover:border-slate-400">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-medium">
                  {u.display_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{u.display_name}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </div>
              </div>
              <span className={`mt-3 inline-block rounded-full px-2 py-0.5 text-xs ${STATUS[u.status] ?? STATUS.active}`}>{u.status}</span>
            </Link>
          ))}
          {items.length === 0 && <p className="text-sm text-slate-500">No users found.</p>}
        </div>
      )}
    </div>
  );
}
